/**
 * Demo image references → bundled image URLs.
 *
 * Interim, until images are managed server-side: demo report datasets name
 * their images (tenant and competition logos, country flags, social-network
 * logos, CDN Gatekeeper screenshots) by *reference*, and the files ship with
 * the WebUI under `src/assets/demo/`. A reference is either
 *
 * - a path relative to that folder, matched case-insensitively
 *   (`"Canal-Logo.png"`, `"country_flags/ml.svg"`), or
 * - a glob with an index (`"GK_thumbnails/clean_*.jpg#4"`): the n-th file, in
 *   name order and wrapping around, among those matching — so a dataset can
 *   ask for "some screenshot" without knowing how many ship.
 *
 * Absolute URLs pass through unchanged, so real image URLs will work the same
 * way once the backend serves them. Unknown references resolve to `undefined`
 * and widgets simply show no image.
 *
 * Country flags are not demo images: they ship with `@nexus/ui`
 * (`packages/ui/assets/country-flags/`), where widgets also read them through
 * `CountryFlag`. A `"country_flags/<code>.svg"` reference resolves there first,
 * then falls back to this folder.
 */
import { countryFlagUrl } from "@nexus/ui";
import { isAbsoluteAssetUrl, type AssetResolver } from "@nexus/widget-sdk";

const PREFIX = "../assets/demo/";

function buildIndex(files: Record<string, string>): Map<string, string> {
  return new Map(
    Object.entries(files)
      .map(
        ([path, url]) =>
          [path.slice(path.indexOf(PREFIX) + PREFIX.length).toLowerCase(), url] as const,
      )
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

function globPattern(glob: string): RegExp {
  const escaped = glob.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, "[^/]*");
  return new RegExp(`^${escaped}$`);
}

const COUNTRY_FLAG = /^country_flags\/([a-z]{2})\.svg$/;

/**
 * A resolver over `files` (a Vite glob of the demo folder: path → URL);
 * `flags` resolves country codes first (the shared `@nexus/ui` catalog).
 */
export function createDemoAssetResolver(
  files: Record<string, string>,
  flags: (code: string) => string | undefined = countryFlagUrl,
): AssetResolver {
  const index = buildIndex(files);
  return (reference) => {
    if (isAbsoluteAssetUrl(reference)) return reference;
    const [path, position] = reference.toLowerCase().split("#", 2);
    const flag = COUNTRY_FLAG.exec(path);
    if (flag) return flags(flag[1]) ?? index.get(path);
    if (!path.includes("*")) return index.get(path);
    const pattern = globPattern(path);
    const matches = [...index].filter(([key]) => pattern.test(key));
    if (matches.length === 0) return undefined;
    const n = Number.parseInt(position ?? "0", 10);
    return matches[(Number.isFinite(n) ? Math.abs(n) : 0) % matches.length][1];
  };
}

export const resolveDemoAsset: AssetResolver = createDemoAssetResolver(
  import.meta.glob("../assets/demo/**/*.{png,jpg,jpeg,svg,webp}", {
    eager: true,
    import: "default",
  }) as Record<string, string>,
);
